import type { TimeTrackerEntry, TimeTrackerProject, TimeTrackerTask } from '@/types/pm-types';

/** Resolve effective rate: task rate > project rate > 0 */
export function resolveRate(
  projects: TimeTrackerProject[],
  tasks: TimeTrackerTask[],
  projectId?: string,
  taskId?: string,
): number {
  if (taskId) {
    const task = tasks.find((t) => t.id === taskId);
    if (task?.billableRate !== undefined) return task.billableRate;
  }
  if (!projectId) return 0;
  const project = projects.find((p) => p.id === projectId);
  if (!project || !project.isBillable) return 0;
  return project.billableRate ?? 0;
}

/** Calculate billable amount for a duration at a given hourly rate */
export function calculateAmount(durationSeconds: number, hourlyRate: number): number {
  return (durationSeconds / 3600) * hourlyRate;
}

/** Format seconds to HH:MM:SS */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Format seconds to decimal hours (e.g., "1.5h") */
export function formatDecimalHours(seconds: number): string {
  const hours = seconds / 3600;
  return `${hours.toFixed(1)}h`;
}

/** Round duration up to nearest N minutes */
export function roundDuration(seconds: number, roundTo: number): number {
  if (seconds === 0) return 0;
  const roundToSeconds = roundTo * 60;
  return Math.ceil(seconds / roundToSeconds) * roundToSeconds;
}

/** Group entries by date (YYYY-MM-DD) */
export function groupEntriesByDate(entries: TimeTrackerEntry[]): Map<string, TimeTrackerEntry[]> {
  const map = new Map<string, TimeTrackerEntry[]>();
  for (const entry of entries) {
    const existing = map.get(entry.date) || [];
    existing.push(entry);
    map.set(entry.date, existing);
  }
  return map;
}

/** Group entries by project ID */
export function groupEntriesByProject(entries: TimeTrackerEntry[]): Map<string, TimeTrackerEntry[]> {
  const map = new Map<string, TimeTrackerEntry[]>();
  for (const entry of entries) {
    const key = entry.projectId || '';
    const existing = map.get(key) || [];
    existing.push(entry);
    map.set(key, existing);
  }
  return map;
}

/** Group entries by client ID (via project lookup) */
export function groupEntriesByClient(
  entries: TimeTrackerEntry[],
  projects: TimeTrackerProject[],
): Map<string, TimeTrackerEntry[]> {
  const map = new Map<string, TimeTrackerEntry[]>();
  for (const entry of entries) {
    const project = projects.find((p) => p.id === entry.projectId);
    const key = project?.clientId || '';
    const existing = map.get(key) || [];
    existing.push(entry);
    map.set(key, existing);
  }
  return map;
}

/** Sum total duration and billable amount for entries */
export function summarizeEntries(
  entries: TimeTrackerEntry[],
  projects: TimeTrackerProject[],
  tasks: TimeTrackerTask[],
): { totalSeconds: number; totalBillable: number; billableSeconds: number } {
  let totalSeconds = 0;
  let billableSeconds = 0;
  let totalBillable = 0;

  for (const entry of entries) {
    totalSeconds += entry.duration;
    if (entry.billable) {
      billableSeconds += entry.duration;
      const rate = resolveRate(projects, tasks, entry.projectId, entry.taskId);
      totalBillable += calculateAmount(entry.duration, rate);
    }
  }

  return { totalSeconds, totalBillable, billableSeconds };
}

/** Map time entries to calendar-compatible events */
export function entriesToCalendarEvents(
  entries: TimeTrackerEntry[],
  projects: TimeTrackerProject[],
): Array<{
  id: string;
  title: string;
  start: Date;
  end: Date;
  color: string;
  projectName: string;
}> {
  return entries.map((entry) => {
    const project = projects.find((p) => p.id === entry.projectId);
    return {
      id: entry.id,
      title: entry.description || '(no description)',
      start: new Date(entry.startTime),
      end: entry.endTime ? new Date(entry.endTime) : new Date(new Date(entry.startTime).getTime() + entry.duration * 1000),
      color: project?.color || '#9ca3af',
      projectName: project?.name || 'No Project',
    };
  });
}

/** Format currency amount */
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}
